import { Injectable } from '@angular/core';
import {
  COUNTRY_BASED_FORM_CONFIG,
  PREFILLED_FIELDS_CONFIG,
  PREFILLED_FIELDS_CONFIG_SHORT,
  SAVE_ADDRESS_COUNTRY_BASED_FORM_CONFIG,
  SAVE_LEGAL_ADDRESS_AS_SHIPPING_AND_BILLING_CHECKBOX,
} from '~app/modules/authorization/constants/registration-tier-two-constants';
import {
  FF_GET_COMPANY_FROM_KVK,
  FF_GET_COMPANY_FROM_VAT_ID,
  FF_SAVE_LEGAL_ADDRESS_AS_BILLING_AND_SHIPPING,
  FF_SL_TIER2_BUSINESS_DETAILS_ADDRESS_AUTOCOMPLETE,
} from '~app/modules/authorization/constants/authorization.constant';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
// prettier-ignore
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { CompanyDetailsFromVatService } from '~app/modules/authorization/services/company-details-from-vat.service';
import {
  COUNTRIES_WITH_DATA_PREFILLING_ON_TIER_TWO,
  COUNTRY_VALUES_FOR_PREFILLING,
  DEFAULT_COUNTRIES_PER_MARKET,
} from '~shared/constants/country-code.enum';
import { CompanyDetailsFromIdService } from '~app/modules/authorization/services/company-details-from-id.service';
import { TierTwoRegistrationData } from '~app/modules/authorization/models/credentials.model';
import { FF_ENABLE_BRANCH_SELECTION_DESIGN } from '../constants/branch-selection.contant';
import { IAdvancedFormConfig } from '~shared/modules/advanced-form/models/advanced-form.model';
import type { IAdvancedFormField } from '~shared/modules/advanced-form/models/advanced-form-fields.model';
import { setGAService } from '~server/utils/auth.utils';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';

@Injectable({
  providedIn: 'root',
})
export class CountryBasedTierTwoConfigService {
  public static isFirstAddressSearchClick = true;
  getCompanyDetailsFromVATId$ =
    this.activeFeaturesConfigCatService.isFeatureEnabledReadyForCurrentCountry$(FF_GET_COMPANY_FROM_VAT_ID);

  getCompanyDetailsFromTaxId$ =
    // TODO: Change FF name to be FF_GET_COMPANY_FROM_TAX_ID and target it to NL and FR
    this.activeFeaturesConfigCatService.isFeatureEnabledReadyForCurrentCountry$(FF_GET_COMPANY_FROM_KVK);

  isSaveLegalAddressAsBillingAndShippingEnabled$ =
    this.activeFeaturesConfigCatService.isFeatureEnabledReadyForCurrentCountry$(
      FF_SAVE_LEGAL_ADDRESS_AS_BILLING_AND_SHIPPING
    );

  companyDetails$ =
    MarketDetectionUtil.isDutchMarket() || MarketDetectionUtil.isFrenchMarket()
      ? this.companyDetailsFromIdService.companyDetailsForTierTwo$
      : this.companyDetailsFromVatService.companyDetailsForTierTwo$;

  isAddressAutocompleteEnabled$ = this.activeFeaturesConfigCatService.isFeatureEnabledReadyForCurrentCountry$(
    FF_SL_TIER2_BUSINESS_DETAILS_ADDRESS_AUTOCOMPLETE
  );

  formConfig = new BehaviorSubject('');
  hasPrefilledCompanyData = new BehaviorSubject<boolean>(false);
  isNewBusinessDetailsDesignEnabled$ = this.featureFlagService.isFeatureEnabledReadyForCurrentCountry$(
    FF_ENABLE_BRANCH_SELECTION_DESIGN
  );

  constructor(
    private activeFeaturesConfigCatService: ActiveFeaturesConfigcatService,
    protected applicationEventService: ApplicationEventService,
    protected companyDetailsFromVatService: CompanyDetailsFromVatService,
    protected companyDetailsFromIdService: CompanyDetailsFromIdService,
    private featureFlagService: ActiveFeaturesConfigcatService,
    private gtmDatalayerService: GtmDatalayerService
  ) {
    const currentCountry = MarketDetectionUtil.getCountryCode();
    this.configBasedOnFF(currentCountry);
  }

  ngOnInit() {
    CountryBasedTierTwoConfigService.isFirstAddressSearchClick = true;
  }

  configBasedOnFF(currentCountry) {
    combineLatest([
      this.getCompanyDetailsFromTaxId$,
      this.getCompanyDetailsFromVATId$,
      this.companyDetails$,
      this.isNewBusinessDetailsDesignEnabled$,
      this.isSaveLegalAddressAsBillingAndShippingEnabled$,
      this.isAddressAutocompleteEnabled$,
    ]).subscribe(
      ([
        isCompanyDetailsFromKVKEnabled,
        isCompanyDetailsFromVATEnabled,
        companyDetails,
        isNewBusinessDetailsDesignEnabled,
        isSaveLegalAddressAsBillingAndShippingEnabled,
        isAddressAutocompleteEnabled,
      ]) => {
        setGAService(this.gtmDatalayerService);

        if (COUNTRIES_WITH_DATA_PREFILLING_ON_TIER_TWO[MarketDetectionUtil.getCountryCode()]) {
          let FORM_CONFIG: IAdvancedFormConfig;

          if (isCompanyDetailsFromVATEnabled || isCompanyDetailsFromKVKEnabled) {
            if (isNewBusinessDetailsDesignEnabled) {
              FORM_CONFIG = PREFILLED_FIELDS_CONFIG_SHORT()[currentCountry];
            } else {
              FORM_CONFIG = PREFILLED_FIELDS_CONFIG()[currentCountry];
            }
          } else {
            FORM_CONFIG = COUNTRY_BASED_FORM_CONFIG({ autocompleteAddress: isAddressAutocompleteEnabled })[
              currentCountry
            ];
          }

          if (isSaveLegalAddressAsBillingAndShippingEnabled) {
            FORM_CONFIG.formSectionList[FORM_CONFIG.formSectionList.length - 1].fieldList = [
              ...FORM_CONFIG.formSectionList[FORM_CONFIG.formSectionList.length - 1].fieldList,
              ...SAVE_LEGAL_ADDRESS_AS_SHIPPING_AND_BILLING_CHECKBOX,
            ] as IAdvancedFormField[];
          }

          // pre-fill form data if company details are available
          const initialFormData = {
            ...(companyDetails !== null
              ? this.setInitialFormData(companyDetails)
              : { legalAddressCountry: DEFAULT_COUNTRIES_PER_MARKET[MarketDetectionUtil.getCountryCode()] }),
            ...(isSaveLegalAddressAsBillingAndShippingEnabled && { saveLegalAddressAsShippingAndBilling: true }),
          };

          this.formConfig.next({
            ...(FORM_CONFIG as any),
            initialFormData,
          });
        } else {
          this.formConfig.next({
            ...SAVE_ADDRESS_COUNTRY_BASED_FORM_CONFIG({ autocompleteAddress: isAddressAutocompleteEnabled })[
            currentCountry
            ],
            initialFormData: {
              saveLegalAddressAsShippingAndBilling: true,
              legalAddressCountry: DEFAULT_COUNTRIES_PER_MARKET[MarketDetectionUtil.getCountryCode()],
            },
          });
        }
      }
    );
  }

  private setInitialFormData(companyDetails): TierTwoRegistrationData {
    this.hasPrefilledCompanyData.next(true);
    let countryWiseData = null;

    const primaryData = {
      legalAddressCity: companyDetails?.legalAddressCity,
      legalAddressBuildingNumber: companyDetails?.legalAddressBuildingNumber,
      businessName: companyDetails?.businessName,
      legalAddressStreet: companyDetails?.legalAddressStreet,
      legalAddressPostalCode: companyDetails?.legalAddressPostalCode,
    };

    if (MarketDetectionUtil.isItalianMarket()) {
      countryWiseData = {
        vatNumber: companyDetails?.vatNumber,
        legalEmailAddress: companyDetails?.legalEmailAddress,
        legalAddressProvince: companyDetails?.legalAddressProvince,
        legalAddressCountry: companyDetails?.legalAddressCountry || DEFAULT_COUNTRIES_PER_MARKET[MarketDetectionUtil.getCountryCode()],
      };
    } else if (MarketDetectionUtil.isPortugueseMarket()) {
      countryWiseData = {
        vatNumber: companyDetails?.vatNumber,
        legalAddressCountry: companyDetails?.legalAddressCountry || DEFAULT_COUNTRIES_PER_MARKET[MarketDetectionUtil.getCountryCode()],
      };
    } else if (MarketDetectionUtil.isDutchMarket()) {
      countryWiseData = {
        kvkNumber: companyDetails?.kvkNumber,
        branchNumber: companyDetails?.branchNumber,
        legalAddressCountry: COUNTRY_VALUES_FOR_PREFILLING.Netherlands,
      };
    } else if (MarketDetectionUtil.isFrenchMarket()) {
      countryWiseData = {
        sirenNumber: companyDetails?.sirenNumber,
        branchNumber: companyDetails?.branchNumber,
        legalAddressCountry: COUNTRY_VALUES_FOR_PREFILLING.France,
      };
    }

    return {
      ...primaryData,
      ...countryWiseData,
    };
  }
}
